import React from 'react';
import cloneDeep from 'lodash.clonedeep';
import { Label } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import { ColorPicker, Input, TextArea } from '@progress/kendo-react-inputs';

import { ReelsTextIcon } from '../../../../../assets';
import { uuid } from '../../../../../types/common-helper';
import { rgbaToHex } from '../../../../../utils/hex-to-rgb';
import { Timeline, useReels } from '../../../../../contexts/reels';

const initialFormValue: Timeline = {
  id: '',
  type: 'text',
  duration_secs: 0
};

export const TextForm: React.FC = () => {
  const {
    textSlides,
    global_settings: { background_color },
    handleChange
  } = useReels();
  const [textSLideForm, setTextSlideForm] =
    React.useState<Timeline>(initialFormValue);
  const [displayError, setDisplayError] = React.useState(false);

  return (
    <div className={'text-form'}>
      <div className={'d-flex gap-3 align-items-center'}>
        <ReelsTextIcon fontSize={24} />
        <b
          style={{
            fontSize: '18px'
          }}>
          Text Slide
        </b>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          padding: '16px',
          borderRadius: '8px',
          backgroundColor: '#F8FCFE'
        }}>
        <Button
          themeColor={'primary'}
          className={'w-100'}
          onClick={() => {
            const textField = [
              textSLideForm.content?.title ?? '',
              textSLideForm.content?.subtitle ?? '',
              textSLideForm.content?.body ?? ''
            ];
            if (textField.every((t) => t === '')) {
              setDisplayError(true);
              return;
            }

            setDisplayError(false);

            let newTextSlides = cloneDeep(textSlides);
            const newItem = cloneDeep(textSLideForm);
            newItem.id = uuid();
            if (!newItem.background_color || newItem.background_color === '') {
              newItem.background_color = background_color;
            }

            newTextSlides = [...newTextSlides, newItem];
            handleChange({ textSlides: newTextSlides });
            setTextSlideForm(initialFormValue);
          }}>
          Create text slide
        </Button>
        {displayError && (
          <span
            className={'text-danger'}
            style={{
              fontSize: '12px'
            }}>
            At least one of the title, subtitle, or body must contain content
            before added to timeline.
          </span>
        )}
      </div>

      <div className={'d-flex flex-column'} style={{ gap: '20px' }}>
        <div>
          <Label>Opening Title</Label>
          <Input
            placeholder={'Enter opening title...'}
            value={textSLideForm.content?.title ?? ''}
            onChange={(e) => {
              setTextSlideForm((prev) => ({
                ...prev,
                content: { ...prev.content, title: e.value }
              }));
            }}
          />
        </div>
        <div>
          <Label>Subtitle</Label>
          <Input
            placeholder={'Enter subtitle...'}
            value={textSLideForm?.content?.subtitle ?? ''}
            onChange={(e) => {
              setTextSlideForm((prev) => ({
                ...prev,
                content: { ...prev.content, subtitle: e.value }
              }));
            }}
          />
        </div>
        <div>
          <Label>Body</Label>
          <TextArea
            rows={4}
            resizable={'none'}
            placeholder={'Enter body text...'}
            value={textSLideForm.content?.body ?? ''}
            onChange={(e) =>
              setTextSlideForm((prev) => ({
                ...prev,
                content: {
                  ...prev.content,
                  body: e.value
                }
              }))
            }
          />
        </div>
        <div className={'d-flex flex-column'}>
          <Label>Background Color</Label>
          <ColorPicker
            views={['gradient']}
            gradientSettings={{
              opacity: false,
              _adaptive: false
            }}
            flatColorPickerSettings={{
              activeView: 'gradient',
              format: 'hex'
            }}
            fillMode={'outline'}
            value={textSLideForm.background_color ?? background_color}
            onChange={(e) =>
              setTextSlideForm((prev) => ({
                ...prev,
                background_color: rgbaToHex(e.value)
              }))
            }
          />
        </div>
        <div>
          <Label>Duration (seconds)</Label>
          <Input
            type={'number'}
            min={0}
            value={textSLideForm?.duration_secs ?? 1}
            onChange={(e) => {
              setTextSlideForm((prev) => ({
                ...prev,
                duration_secs: parseInt(e.value)
              }));
            }}
          />
        </div>
      </div>
    </div>
  );
};
